import { Component, OnInit, OnDestroy } from '@angular/core';
import { PatientService } from '../../../core/api/patient.service';
import { PAMPRestAPIService } from '../../../core/api/pamp-rest-api.service';
import { of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { ActivatedRoute } from '@angular/router';
import { PampIframeService } from 'src/app/core/services/pamp-iframe.service';
import { OrderRequest, Product } from '../../../core/models/order-request.model';
import { safeParseJson } from 'src/app/shared/util/helper.util';
import { LocaleDatePipe } from 'src/app/shared/shared-pipes/locale-date.pipe';
import { RecordDetailHost } from '../record-detail-panel/record-detail-host';
import { SideSheetService } from '../../../shared/side-sheet/side-sheet.service';
import { ChipColour } from '../../../shared/chip/chip.component';
import { OrderRow, OrderSortField, OrderStatusCount } from './order-row.model';
import { OrderDetailAction, OrderDetailSheetComponent } from './order-detail-sheet.component';

const EMPTY_VALUE = '—';

@Component({
  selector: 'csi-order-requests-table',
  templateUrl: './order-requests-table.component.html',
  styleUrls: ['./order-requests-table.component.scss'],
  providers: [LocaleDatePipe]
})
export class OrderRequestsTableComponent extends RecordDetailHost<string>
  implements OnInit, OnDestroy {
  public isLoading = true;
  public hasError = false;
  public isPatientAppPaused = false;

  private readonly MAX_VISIBLE_PRODUCTS = 3;

  // The dense table rows render a plain dot via the app-wide badge palette; the detail sheet
  // renders a filled csi-chip. One map so a status can never drift between the two.
  private readonly statusBadgeStyleMap: {
    [status: string]: { badgeClass: string; chipColour: ChipColour };
  } = {
    PENDING: { badgeClass: 'badge-amber', chipColour: 'amber-no-outline' },
    ACCEPTED: { badgeClass: 'badge-teal', chipColour: 'teal' },
    IN_PROGRESS: { badgeClass: 'badge-indigo', chipColour: 'indigo-no-outline' },
    SHIPPED: { badgeClass: 'badge-blue', chipColour: 'blue-no-outline' },
    COMPLETED: { badgeClass: 'badge-green', chipColour: 'green-no-outline' },
    CANCELLED: { badgeClass: 'badge-red', chipColour: 'red-no-outline' }
  };
  private readonly defaultStatusBadgeStyle: { badgeClass: string; chipColour: ChipColour } = {
    badgeClass: 'badge-gray',
    chipColour: 'grey-no-outline'
  };

  public orderRows: OrderRow[] = [];
  public statusCounts: OrderStatusCount[] = [];

  public sortField: OrderSortField = 'requestedOn';
  public isSortDescending = true;

  private readonly patientIdToFetch = new Subject<string>();

  constructor(
    public patientService: PatientService,
    private pampRestAPIService: PAMPRestAPIService,
    private pampIframeService: PampIframeService,
    private route: ActivatedRoute,
    private localeDatePipe: LocaleDatePipe,
    sideSheetService: SideSheetService
  ) {
    super(sideSheetService);
  }

  ngOnInit() {
    this.listenForOrderFetches();
    this.updatePatientAppPausedStatus();
    if (this.route.parent) {
      this.route.parent.params.pipe(untilDestroyed(this)).subscribe(params => {
        const patientId = params['id'];
        this.clearOrderData();
        this.updatePatientAppPausedStatus();
        if (patientId) {
          this.fetchOrderRequests(patientId);
        } else {
          this.isLoading = false;
        }
      });
    } else {
      this.isLoading = false;
    }

    this.pampIframeService.iframeClosed$.pipe(untilDestroyed(this)).subscribe(() => {
      this.refreshData();
    });
  }

  ngOnDestroy() {
    this.closeDetail();
  }

  public openPatientApp() {
    if (!this.patientService.patient) {
      return;
    }

    this.pampIframeService.openPatientDashboard(this.patientService.patient.id);
  }

  public createOrderRequest() {
    if (this.isPatientAppPaused) {
      return;
    }

    if (!this.patientService.patient || !this.patientService.patient.id) {
      return;
    }

    this.pampIframeService.openPatientDashboard(this.patientService.patient.id, {
      createOrderRequest: true
    });
  }

  public editOrderRequest(orderId: string) {
    this.openOrderInPatientApp(orderId, false);
  }

  public viewOrderRequestHistory(orderId: string) {
    this.openOrderInPatientApp(orderId, true);
  }

  private openOrderInPatientApp(orderId: string, shouldShowHistory: boolean) {
    if (!orderId || !this.patientService.patient) {
      return;
    }
    const queryParams: { [key: string]: any } = {
      patientId: this.patientService.patient.id,
      orderId: orderId
    };
    if (shouldShowHistory) {
      queryParams.history = 'true';
    }
    this.pampIframeService.openProductRefillRequests(queryParams);
  }

  public refreshData() {
    this.updatePatientAppPausedStatus();
    if (this.patientService.patient && this.patientService.patient.id) {
      this.fetchOrderRequests(this.patientService.patient.id);
    }
  }

  private updatePatientAppPausedStatus(): void {
    const patient = this.patientService && this.patientService.patient;
    const enrollmentConfig = patient ? safeParseJson(patient.patientAppEnrollmentConfig) : null;
    const status = enrollmentConfig ? enrollmentConfig.status : null;
    this.isPatientAppPaused = status ? status.toLowerCase() === 'paused' : false;
  }

  private toNormalizedStatus(status: string | undefined | null): string {
    return status
      ? String(status)
          .toUpperCase()
          .trim()
          .replace(/[\s-]+/g, '_')
      : '';
  }

  private getStatusBadgeStyle(
    status: string | undefined | null
  ): { badgeClass: string; chipColour: ChipColour } {
    if (!status) {
      return this.defaultStatusBadgeStyle;
    }
    return (
      this.statusBadgeStyleMap[this.toNormalizedStatus(status)] || this.defaultStatusBadgeStyle
    );
  }

  private readonly statusDisplayOrder = [
    'PENDING',
    'ACCEPTED',
    'IN_PROGRESS',
    'SHIPPED',
    'COMPLETED',
    'CANCELLED'
  ];

  private readonly statusLabelMap: { [status: string]: string } = {
    PENDING: 'Pending',
    ACCEPTED: 'Accepted',
    IN_PROGRESS: 'In progress',
    SHIPPED: 'Shipped',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled'
  };

  public openOrderDetail(orderRow: OrderRow) {
    const sheetReference = this.toggleDetail<OrderRow, OrderDetailAction>(
      orderRow.orderId,
      () => orderRow,
      OrderDetailSheetComponent
    );
    if (!sheetReference) {
      return;
    }
    // The patient-app handoff stays here so there is one implementation of it, not a second copy
    // inside the sheet.
    sheetReference.afterClosed().subscribe(action => {
      if (action === 'openInPatientApp') {
        this.viewOrderRequestHistory(orderRow.orderId);
        return;
      }
      if (action === 'edit') {
        this.editOrderRequest(orderRow.orderId);
      }
    });
  }

  public trackByOrderId(index: number, orderRow: OrderRow): string {
    return orderRow.orderId;
  }

  public trackByProductName(index: number, productName: string): string {
    return index + ':' + productName;
  }

  public sortByColumn(field: OrderSortField): void {
    if (this.sortField === field) {
      this.isSortDescending = !this.isSortDescending;
    } else {
      this.sortField = field;
      this.isSortDescending = field === 'requestedOn';
    }
    this.orderRows = this.sortOrderRows(this.orderRows);
  }

  private sortOrderRows(orderRows: OrderRow[]): OrderRow[] {
    return orderRows.slice().sort((firstRow, secondRow) => {
      const comparison = this.compareBySortField(firstRow, secondRow);
      return this.isSortDescending ? -comparison : comparison;
    });
  }

  private compareBySortField(firstRow: OrderRow, secondRow: OrderRow): number {
    switch (this.sortField) {
      case 'products':
        return this.compareText(
          firstRow.allProductNames.join(', '),
          secondRow.allProductNames.join(', ')
        );
      case 'updatedBy':
        return this.compareText(firstRow.updatedByLabel, secondRow.updatedByLabel);
      default:
        return this.compareDate(firstRow.requestedOn, secondRow.requestedOn);
    }
  }

  private compareText(firstValue: string, secondValue: string): number {
    if (!firstValue || !secondValue) {
      return this.compareEmptiness(firstValue, secondValue);
    }
    return firstValue.toLowerCase().localeCompare(secondValue.toLowerCase());
  }

  private compareDate(firstValue: string, secondValue: string): number {
    const firstTime = firstValue ? new Date(firstValue).getTime() : NaN;
    const secondTime = secondValue ? new Date(secondValue).getTime() : NaN;
    if (isNaN(firstTime) || isNaN(secondTime)) {
      return this.compareEmptiness(isNaN(firstTime) ? '' : 'set', isNaN(secondTime) ? '' : 'set');
    }
    return firstTime - secondTime;
  }

  // A row with no value sorts last whichever way the column points, so an empty cell never
  // outranks a real one. The caller negates when descending, hence the flip here.
  private compareEmptiness(firstValue: string, secondValue: string): number {
    if (!firstValue && !secondValue) {
      return 0;
    }
    const emptyGoesLast = firstValue ? -1 : 1;
    return this.isSortDescending ? -emptyGoesLast : emptyGoesLast;
  }

  private buildOrderRow(order: OrderRequest): OrderRow {
    const productNames = this.getProductNames(order);
    const status = order.status ? String(order.status).toUpperCase() : '';

    const requestedOn = order.requestDate || order.createdAt;

    return {
      orderId: order.id,
      allProductNames: productNames,
      visibleProductNames: productNames.slice(0, this.MAX_VISIBLE_PRODUCTS),
      hiddenProductCount: Math.max(0, productNames.length - this.MAX_VISIBLE_PRODUCTS),
      status: this.toNormalizedStatus(order.status),
      statusBadgeClass: this.getStatusBadgeStyle(order.status).badgeClass,
      statusChipColour: this.getStatusBadgeStyle(order.status).chipColour,
      statusLabel: this.buildStatusLabel(order.status),
      requestedOn: requestedOn,
      requestedOnLabel: this.formatDate(requestedOn),
      processedOnLabel: this.formatDate(order.processedAt),
      updatedOnLabel: this.formatDate(order.updatedAt),
      hasUpdatedOn: !!order.updatedAt,
      hasBeenProcessed: !!order.processedAt,
      updatedByLabel: order.processedBy || '',
      clinicNote: order.clinicalActionNote || '',
      patientNote: order.notes || '',
      notePreview: order.notes || order.clinicalActionNote || '',
      patientNoteSummaryKey: order.notes ? '1 patient note' : 'no patient note',
      clinicNoteSummaryKey: order.clinicalActionNote ? '1 clinic note' : 'no clinic note',
      canEdit: status !== 'COMPLETED' && status !== 'CANCELLED'
    };
  }

  private buildStatusLabel(status: string): string {
    if (!status) {
      return '';
    }
    const normalizedStatus = String(status).toUpperCase();
    if (this.statusLabelMap[normalizedStatus]) {
      return this.statusLabelMap[normalizedStatus];
    }
    const spacedStatus = normalizedStatus
      .replace(/_/g, ' ')
      .toLowerCase()
      .trim();
    return spacedStatus.charAt(0).toUpperCase() + spacedStatus.slice(1);
  }

  private formatDate(dateValue: string): string {
    const formattedDate = this.localeDatePipe.transform(dateValue, 'MMM d, y');
    return formattedDate ? formattedDate : EMPTY_VALUE;
  }

  private getProductNames(order: OrderRequest): string[] {
    const parsedProducts = safeParseJson(order.products);
    if (!Array.isArray(parsedProducts)) {
      return [];
    }
    return parsedProducts
      .filter(Boolean)
      .map((product: Product) => product.productName || 'Unknown Product');
  }

  public trackByStatusCount(index: number, statusCount: OrderStatusCount): string {
    return statusCount.status;
  }

  private refreshStatusCounts(): void {
    this.statusCounts = this.statusDisplayOrder
      .map(status => ({
        status: status,
        statusLabel: this.statusLabelMap[status] || status,
        badgeClass: this.getStatusBadgeStyle(status).badgeClass,
        count: this.orderRows.filter(orderRow => orderRow.status === status).length
      }))
      .filter(statusCount => statusCount.count > 0);
  }

  private clearOrderData() {
    this.orderRows = [];
    this.refreshStatusCounts();
    this.closeDetail();
  }

  private fetchOrderRequests(patientId: string) {
    this.clearOrderData();
    this.isLoading = true;
    this.hasError = false;
    this.patientIdToFetch.next(patientId);
  }

  private listenForOrderFetches() {
    this.patientIdToFetch
      .pipe(
        switchMap(patientId =>
          this.pampRestAPIService.getPAMPPatientOrderRequests(patientId).pipe(
            map(orderRequests => ({ orderRequests: orderRequests, hasFailed: false })),
            catchError(error => {
              console.error(
                'Error fetching order requests',
                error && error.error && error.error.message ? error.error.message : error
              );
              return of({ orderRequests: [] as OrderRequest[], hasFailed: true });
            })
          )
        ),
        untilDestroyed(this)
      )
      .subscribe(fetchResult => {
        if (fetchResult.hasFailed) {
          this.hasError = true;
          this.isLoading = false;
          return;
        }
        const orderRequests = Array.isArray(fetchResult.orderRequests)
          ? fetchResult.orderRequests
          : [];
        this.orderRows = this.sortOrderRows(
          orderRequests.map(order => this.buildOrderRow(order))
        );
        this.refreshStatusCounts();
        this.isLoading = false;
      });
  }
}
